import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "@/utils/appsettings";
import type { FigmaRecommendation } from "@/hooks/useFigmaPlugin";
import type { FigmaRef } from "@/utils/figmaUrl";

// Client-side view of the Figma integration. Mirrors the shapes returned by
// Server/src/routes/figma.py.

export interface FigmaConnection {
  connected: boolean;
  configured: boolean;
  handle?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  seatType?: string | null;
  plan?: string | null;
  hasVariablesAccess?: boolean;
  /**
   * False for Figma View and Collab seats, whose read allowance is roughly six a
   * month — under one import. Surfaced before the user starts so the limit is a
   * message rather than a failure halfway through.
   */
  seatSupportsImport?: boolean;
  expiresAt?: string | null;
  isActive?: boolean;
  /** The access token is past its expiry and could not be refreshed. */
  isExpired?: boolean;
  /**
   * The account has to be authorised again before an import can run. Reported by the API rather
   * than inferred here: a token inside its refresh margin is not dead, and treating it as dead
   * would push the user through a reconnect they do not need.
   */
  needsReconnect?: boolean;
}

/** What a frame actually is. A real file is mostly not screens. */
export type FigmaFrameKind =
  | "screen"
  | "long_page"
  | "board"
  | "graphic"
  | "sheet"
  | "assets"
  | "element"
  | "unknown";

export interface FigmaFrame {
  id: string;
  name: string;
  pageId: string;
  pageName: string;
  width: number;
  height: number;
  device: "mobile" | "tablet" | "desktop" | "unknown";
  kind?: FigmaFrameKind;
  /** False for boards, icon sheets and social graphics — building them wastes the turn. */
  buildable?: boolean;
  /** True for frames worth selecting by default. */
  recommended?: boolean;
  /** Plain-English explanation shown when a frame is not a screen. */
  reason?: string;
}

export interface FigmaSelectionSummary {
  total: number;
  buildable: number;
  recommended: number;
  skipped: number;
  by_kind: Record<string, number>;
}

export interface FigmaPage {
  id: string;
  name: string;
  frames: FigmaFrame[];
}

export interface FigmaOutline {
  fileKey: string;
  name: string;
  version: string;
  frameCount: number;
  thumbnailUrl: string | null;
  pages: FigmaPage[];
  selectedNodeId: string | null;
  notice?: string;
  /** Counts of what the file contains, so the picker can lead with the buildable frames. */
  selection?: FigmaSelectionSummary;
  /** What the file's frame sizes say it should be built as; the selection refines it. */
  recommendation?: FigmaRecommendation;
}

const errorMessage = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: { error?: { message?: string } } } } })
    ?.response?.data?.detail?.error?.message;
  return detail || fallback;
};

export const useFigmaConnection = () => {
  const [connection, setConnection] = useState<FigmaConnection | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get("/figma/connection");
      setConnection(res.data?.data ?? null);
    } catch (err) {
      setError(errorMessage(err, "Could not check your Figma connection."));
      setConnection(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The popup finishes the exchange and posts back, because nothing else tells this screen the
  // connection is live — without it the status sits on "Not connected" until a manual reload
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if ((event.data as { type?: string })?.type === "joylo:figma-connected") void refresh();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [refresh]);

  const connect = useCallback(async () => {
    setError(null);
    try {
      const res = await axiosInstance.get("/figma/auth/initiate");
      const url = res.data?.data?.authorizationUrl;
      if (!url) {
        setError("Could not start the Figma connection.");
        return;
      }
      // Opened in a popup so an in-progress prompt is not lost to a full redirect
      window.open(url, "figma-oauth", "width=680,height=800");
    } catch (err) {
      setError(errorMessage(err, "Could not start the Figma connection."));
    }
  }, []);

  const disconnect = useCallback(async () => {
    try {
      await axiosInstance.delete("/figma/connection");
      await refresh();
    } catch (err) {
      setError(errorMessage(err, "Could not disconnect Figma."));
    }
  }, [refresh]);

  // Switching accounts, and the only way out of an expired connection. The stored connection is
  // dropped first so the callback writes a fresh one rather than updating the old row in place.
  const reconnect = useCallback(async () => {
    setError(null);
    try {
      await axiosInstance.delete("/figma/connection");
    } catch (err) {
      // Not fatal: re-authorising overwrites the connection anyway, and failing here would leave
      // the user stuck on exactly the expired connection they are trying to replace.
      setError(errorMessage(err, "Could not clear the previous Figma connection."));
    }
    await refresh();
    await connect();
  }, [refresh, connect]);

  return { connection, loading, error, refresh, connect, disconnect, reconnect };
};

export const useFigmaResolve = () => {
  const [outline, setOutline] = useState<FigmaOutline | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guards against a stale response from an earlier link overwriting a newer one
  const requestId = useRef(0);

  const resolve = useCallback(async (url: string) => {
    const current = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.post("/figma/resolve", { url });
      if (current !== requestId.current) return null;
      const data = res.data?.data as FigmaOutline;
      setOutline(data);
      return data;
    } catch (err) {
      if (current !== requestId.current) return null;
      setError(errorMessage(err, "Could not read that Figma file."));
      setOutline(null);
      return null;
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    requestId.current += 1;
    setOutline(null);
    setError(null);
    setLoading(false);
  }, []);

  return { outline, loading, error, resolve, reset };
};

export const useFigmaImport = () => {
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startImport = useCallback(
    async (params: {
      projectId: string;
      ref: FigmaRef;
      nodeIds: string[];
      fileName?: string;
      prompt?: string;
    }) => {
      setImporting(true);
      setError(null);
      try {
        await axiosInstance.post("/figma/import", {
          project_id: params.projectId,
          file_key: params.ref.fileKey,
          node_ids: params.nodeIds,
          file_name: params.fileName ?? "",
          prompt: params.prompt ?? "",
        });
        return true;
      } catch (err) {
        setError(errorMessage(err, "Could not start the Figma import."));
        return false;
      } finally {
        setImporting(false);
      }
    },
    [],
  );

  return { importing, error, startImport };
};
